import moment from 'moment'
import { EnumEstadoFactura } from '../../api'
import { apiGetClientDataByCUPS } from '../../api/Argentia/endpoints/contracts/contracts.api'
import {
  apiDeleteObjection,
  apiFetchCriticalityTypes,
  apiFetchInvoices,
  apiFetchObjectionsByInvoiceID,
  apiFetchObjectionsDescriptions,
  apiGenerateCancellationConcepts,
  apiGenerateFloatingConceptsInvoice,
  apiGetDeliveryNotes,
  apiGetInvoiceAnnexedParam,
  apiGetInvoiceByExecutionCycleID,
  apiGetInvoiceById,
  apiGetInvoiceDetailsById,
  apiGetInvoicesByEmissionDate,
  apiGetProductConcepts,
  apiPostInvoiceAnnexedParam,
  apiPutInvoiceAnnexedParam,
  apiRevertInvoicesToDraft,
  apiSaveObjection
} from '../../api/Argentia/endpoints/invoices/invoices.api'
import {
  GenerateInvoiceFloatingConceptModel,
  InvoiceAnnexedParamModel,
  InvoiceDetailResponseModel,
  InvoiceResponseModel
} from '../../api/Argentia/endpoints/invoices/model/invoices'
import { SettlementTableModel } from '../../api/Argentia/endpoints/settlements/model/settlements'
import { ProductConceptModel } from '../../api/Argentia/model/contracts'
import { EnumDominioTarea } from '../../api/oData/enums/enumDominioTarea'
import { enumKeyToValue } from '../../helpers'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import i18n from '../../i18n'
import { ActionIcons, DataTableActionConfiguration } from '../../model'
import { ApiError } from '../../model/apiError'
import { ServiceResponse } from '../../model/serviceResponse'
import { SubjectModel } from '../clients/clients.model'
import { ClosedSettlementsSearchRequest } from '../settlements/settlements.models'
import {
  invoiceFilterMapper,
  invoicesItemsMapper,
  mapSettlementTableApiResponse
} from './invoicePage.mapper'
import { InvoiceFilterRequest, InvoicesTableModel, ObjectionTableModel } from './invoicesPage.Model'

export const fetchInvoices = async ({
  commodity,
  clientType,
  dateRange,
  invoiceCode,
  subjectCode,
  contractCode,
  cupsCode,
  activeDateFilter,
  filterDateBy = 1,
  invoiceStates = null,
  activePageTab = null,
  subjectId,
  hasObjections = null
}: InvoiceFilterRequest): Promise<ServiceResponse<any>> => {
  return await apiFetchInvoices(
    invoiceFilterMapper({
      commodity,
      clientType,
      dateRange,
      invoiceCode,
      subjectCode,
      contractCode,
      cupsCode,
      activeDateFilter,
      filterDateBy,
      invoiceStates,
      activePageTab,
      subjectId,
      hasObjections
    })
  )
    .then(response => {
      return createSuccessServiceResponse(invoicesItemsMapper(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchCriticalityTypes = async (): Promise<ServiceResponse<any>> => {
  return await apiFetchCriticalityTypes()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchObjectionsDescriptions = async (
  taskDomain?: EnumDominioTarea
): Promise<ServiceResponse<any>> => {
  return await apiFetchObjectionsDescriptions(taskDomain)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchObjectionsByInvoiceID = async (
  invoiceID: number
): Promise<ServiceResponse<any>> => {
  return await apiFetchObjectionsByInvoiceID(invoiceID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const saveObjection = async (
  objection: ObjectionTableModel,
  invoiceId?: number,
  settlementID?: number
): Promise<ServiceResponse<any>> => {
  if (settlementID) {
    objection.settlementID = settlementID
  } else if (invoiceId) {
    objection.invoiceID = invoiceId
  } else {
    const error: ApiError = {
      statusCode: 400,
      errors: ['Either settlementID or invoiceId must be provided']
    }
    return createErrorServiceResponse(error)
  }
  return await apiSaveObjection(objection)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const canManual = (item: InvoicesTableModel): boolean => {
  return (
    item.lgManual && item.status !== enumKeyToValue(EnumEstadoFactura, EnumEstadoFactura.Emitida)
  )
}

export const deleteObjection = async (objectionID: number): Promise<ServiceResponse<any>> => {
  return await apiDeleteObjection(objectionID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}
//#region Operation global actions

export const filterApplicableActionsConfiguration = (
  item: InvoicesTableModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = []
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.ERROR: {
        if (item.hasObjections) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
    }
  })

  return applicableActionsConfiguration
}

export const getGlobalActionsConfiguration = (): Array<DataTableActionConfiguration> => {
  return [getObjectionsDisagreementAction()]
}

const getObjectionsDisagreementAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.ERROR,
    tooltip: i18n.t('invoicesPage:table.tooltips.objectionsError')
  }
}

//#endregion

export const fetchInvoiceDetails = async (
  invoiceID: number,
  expand?: boolean
): Promise<ServiceResponse<InvoiceResponseModel | undefined>> => {
  return await apiGetInvoiceById(invoiceID, expand)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchDeliveryNotes = async (
  invoiceID: number
): Promise<ServiceResponse<InvoiceResponseModel[] | undefined>> => {
  return await apiGetDeliveryNotes(invoiceID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchClientDataByCUPS = async (
  CUPSCode: string,
  startDate: Date,
  endDate: Date
): Promise<ServiceResponse<SubjectModel | undefined>> => {
  return await apiGetClientDataByCUPS(
    CUPSCode,
    moment(startDate).format('YYYY-MM-DD'),
    moment(endDate).format('YYYY-MM-DD')
  )
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

// Generate Invoice by Floating Concepts
export const generateFloatingConceptsInvoice = async (
  floatingConcepts: GenerateInvoiceFloatingConceptModel
): Promise<ServiceResponse<GenerateInvoiceFloatingConceptModel | undefined>> => {
  return await apiGenerateFloatingConceptsInvoice(floatingConcepts)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getInvoiceByExecutionCycleID = async (
  executionCycleId: number,
  issuedOnly: boolean,
  subjectType?: number
): Promise<ServiceResponse<SettlementTableModel[] | undefined>> => {
  return await apiGetInvoiceByExecutionCycleID(executionCycleId, issuedOnly, subjectType)
    .then(response => {
      return createSuccessServiceResponse(mapSettlementTableApiResponse(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const revertInvoicesToDraft = async (
  invoiceIds: number[]
): Promise<ServiceResponse<void | undefined>> => {
  return await apiRevertInvoicesToDraft(invoiceIds)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getInvoicesByEmissionDate = async (
  request: ClosedSettlementsSearchRequest
): Promise<ServiceResponse<SettlementTableModel[] | undefined>> => {
  return await apiGetInvoicesByEmissionDate(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchInvoiceAnnexedParam = async (
  invoiceID: number
): Promise<ServiceResponse<InvoiceAnnexedParamModel[] | undefined>> => {
  return await apiGetInvoiceAnnexedParam(invoiceID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const addInvoiceAnnexedParam = async (
  invoiceID: number,
  request: InvoiceAnnexedParamModel
): Promise<ServiceResponse<InvoiceAnnexedParamModel | undefined>> => {
  return await apiPostInvoiceAnnexedParam(invoiceID, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateInvoiceAnnexedParam = async (
  invoiceID: number,
  invoiceAnnexedParamId: number,
  request: InvoiceAnnexedParamModel
): Promise<ServiceResponse<InvoiceAnnexedParamModel | undefined>> => {
  return await apiPutInvoiceAnnexedParam(invoiceID, invoiceAnnexedParamId, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const generateCancellationConcepts = async (
  invoiceIDs: number[],
  recalcularPenalizaciones: boolean
): Promise<ServiceResponse<number[] | undefined>> => {
  return await apiGenerateCancellationConcepts(invoiceIDs, recalcularPenalizaciones)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchManualInvoiceDetails = async (
  invoiceID: number
): Promise<ServiceResponse<InvoiceDetailResponseModel | undefined>> => {
  return await apiGetInvoiceDetailsById(invoiceID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchAddConceptSelectOptions = async (
  productIds: number[]
): Promise<ServiceResponse<ProductConceptModel[] | undefined>> => {
  return await apiGetProductConcepts(productIds)
    .then(response => createSuccessServiceResponse(response))
    .catch(error => createErrorServiceResponse(error))
}
